import { useEffect, useRef } from "react";

/**
 * VersionWatcher — silently polls /api/version every 60s. When the build
 * version changes from the version that loaded the current tab, the page
 * is reloaded with cache-bust so the user gets the latest deploy without
 * having to clear browser cache manually.
 *
 * Renders nothing; lives in the component tree purely for its effect.
 *
 * The watcher is intentionally quiet — no toast, no banner. Users on
 * preview test new content frequently; we don't want a "new version"
 * popup blocking the page mid-thought. We reload only when the tab is
 * idle (no input focus, last user activity > 30s ago).
 */
export default function VersionWatcher() {
    const initialVersion = useRef(null);
    const lastActivity = useRef(Date.now());

    useEffect(() => {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        if (!apiBase) return;

        const markActivity = () => {
            lastActivity.current = Date.now();
        };
        window.addEventListener("pointerdown", markActivity);
        window.addEventListener("keydown", markActivity);

        const check = async () => {
            try {
                // bust intermediate caches with a query param so we always
                // hit the live backend, never a stale CDN response.
                const res = await fetch(
                    `${apiBase}/api/version?ts=${Date.now()}`,
                    { cache: "no-store", credentials: "include" },
                );
                if (!res.ok) return;
                const data = await res.json();
                const v = data?.version;
                if (!v) return;
                if (initialVersion.current === null) {
                    initialVersion.current = v;
                    return;
                }
                if (v !== initialVersion.current) {
                    // Don't yank the page while the user is mid-thought.
                    const idleFor = Date.now() - lastActivity.current;
                    const focused = document.activeElement;
                    const typingInInput =
                        focused &&
                        (focused.tagName === "TEXTAREA" ||
                            focused.tagName === "INPUT");
                    if (!typingInInput && idleFor > 30000) {
                        window.location.reload();
                    }
                }
            } catch {
                // Network blips are fine — we'll try again next interval.
            }
        };

        // First check after 5s; subsequent checks every 60s.
        const initial = setTimeout(check, 5000);
        const id = setInterval(check, 60000);
        return () => {
            clearTimeout(initial);
            clearInterval(id);
            window.removeEventListener("pointerdown", markActivity);
            window.removeEventListener("keydown", markActivity);
        };
    }, []);

    return null;
}
